import { useState } from "react"
import { Check, X, Star } from "lucide-react"
import { useGetReviewsQuery, useModerateReviewMutation } from "../features/admin/adminApi"

export default function ReviewsPage() {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState("pending")

  const { data, isLoading } = useGetReviewsQuery({ page, status })
  const [moderateReview] = useModerateReviewMutation()

  const reviews = data?.reviews ?? []
  const totalPages = data?.totalPages ?? 1

  const handleModerate = async (uid: string, action: "approved" | "rejected") => {
    await moderateReview({ uid, action })
  }

  const statusBadge = (s: string) => {
    switch (s) {
      case "approved": return "bg-green-100 text-green-700"
      case "rejected": return "bg-red-100 text-red-700"
      default: return "bg-yellow-100 text-yellow-700"
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Reviews</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1) }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No {status} reviews.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {reviews.map((r) => (
              <div key={r.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={13} className={i < r.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
                        ))}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(r.status)}`}>
                        {r.status}
                      </span>
                      <span className="text-xs text-gray-400 capitalize">{r.reviewableType}</span>
                    </div>
                    <p className="text-sm text-gray-800 mb-1">{r.comment || <em className="text-gray-400">No comment</em>}</p>
                    <p className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleDateString()}</p>
                  </div>

                  {r.status === "pending" && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleModerate(r.id, "approved")}
                        title="Approve"
                        className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => handleModerate(r.id, "rejected")}
                        title="Reject"
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Previous</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
